import { useEffect, useState } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import OutlinedFieldContainer from '../OutlinedFieldContainer';

export const IMAGE_FILE_ACCEPT = 'image/jpeg,image/png,image/webp';

export interface RhfFileInputProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  accept?: string;
  required?: boolean;
  height?: number | string;
  initialPreviewSrc?: string | null;
}

function RhfFileInput<T extends FieldValues>({
  name,
  label,
  accept,
  required,
  height = 200,
  initialPreviewSrc,
}: RhfFileInputProps<T>) {
  const { control } = useFormContext<T>();
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: required ? { required: `${label} is required` } : undefined,
  });
  const selectedFile = (field.value as File | null) ?? null;
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewSrc(initialPreviewSrc ?? null);
      return;
    }
    const nextUrl = URL.createObjectURL(selectedFile);
    setPreviewSrc(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [initialPreviewSrc, selectedFile]);

  return (
    <OutlinedFieldContainer label={label} error={error}>
      <Box
        component="label"
        sx={{
          position: 'relative',
          display: 'block',
          mt: -1,
          height,
          borderRadius: 1,
          overflow: 'hidden',
          cursor: 'pointer',
          '&:hover .file-overlay': {
            opacity: 1,
          },
        }}
      >
        <input
          type="file"
          hidden
          accept={accept}
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            field.onChange(file);
          }}
          onBlur={field.onBlur}
          ref={field.ref}
        />

        {previewSrc ? (
          <Box
            component="img"
            src={previewSrc}
            sx={{
              m: 1,
              width: 'calc(100% - 16px)',
              height: 'calc(100% - 16px)',
              objectFit: 'contain',
            }}
          />
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Upload File
          </Typography>
        )}

        <Box
          className="file-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.05)',
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.25s ease',
          }}
        >
          {previewSrc && (
            <Typography variant="body2" color="text.secondary">
              Replace File
            </Typography>
          )}
        </Box>
      </Box>
    </OutlinedFieldContainer>
  );
}

export default RhfFileInput;
